import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { TextInput } from "../../../../form/textInput";
import { Textarea } from "../../../../form/textarea";
import { updateNodeValue } from "../../../../../services/method";
import { webURL } from "../../../../../services/API";
import { operationType, selectDataType } from "../../../../../services/types";
import { AuthenticateSelect } from "../../../../auth/select";
import { Select } from "../../../../form/select";
import { Main, Styled } from "./style";
import { Switch } from "../../../../form/switch";
import { Checkboxs } from "../../../../form/checkboxs";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type OdooProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

type OdooResource = 'lead' | 'note' | 'contact' | 'productTemplate' | 'productVariant' | 'order' | 'customer' | 'inventory';

// Resource options
const resourceOptionsBase: selectDataType[] = [
   { id: 1, name: 'Lead', refId: 'lead', isActive: true },
   { id: 2, name: 'Note', refId: 'note' },
   { id: 3, name: 'Contact', refId: 'contact' },
   { id: 4, name: 'Product Template', refId: 'productTemplate' },
   { id: 5, name: 'Product Variant', refId: 'productVariant' },
   { id: 6, name: 'Order', refId: 'order' },
   { id: 7, name: 'Customer', refId: 'customer' },
   { id: 8, name: 'Inventory', refId: 'inventory' },
];

// Request options per resource
const leadRequests: selectDataType[] = [
   { id: 1, name: 'Fetch Attachments', refId: 'fetch_attachments', isActive: true },
   { id: 2, name: 'Fetch Leads', refId: 'fetch_leads' },
   { id: 3, name: 'Get Lead Details', refId: 'lead_details' },
   { id: 4, name: 'New Lead', refId: 'new_lead' },
   { id: 5, name: 'Update Lead', refId: 'update_lead' },
   { id: 6, name: 'Delete Lead', refId: 'delete_lead' },
   { id: 7, name: 'Let AI Agent Decide', refId: 'agent_decide' },
];

const noteRequests: selectDataType[] = [
   { id: 1, name: 'Get All Notes', refId: 'get_all_notes', isActive: true },
   { id: 2, name: 'Get Note', refId: 'get_note' },
   { id: 3, name: 'Create Note', refId: 'create_note' },
   { id: 4, name: 'Update Note', refId: 'update_note' },
   { id: 5, name: 'Delete Note', refId: 'delete_note' },
   { id: 7, name: 'Let AI Agent Decide', refId: 'agent_decide' },
];

const contactRequests: selectDataType[] = [
   { id: 1, name: 'Get All Contacts', refId: 'get_all_contacts', isActive: true },
   { id: 2, name: 'Get Contact', refId: 'get_contact' },
   { id: 3, name: 'Create Contact', refId: 'create_contact' },
   { id: 4, name: 'Update Contact', refId: 'update_contact' },
   { id: 5, name: 'Delete Contact', refId: 'delete_contact' },
   { id: 7, name: 'Let AI Agent Decide', refId: 'agent_decide' },
];

const productTemplateRequests: selectDataType[] = [
   { id: 1, name: 'Create Product Template', refId: 'createProductTemplate', isActive: true },
   { id: 2, name: 'Get Product Template', refId: 'getProductTemplate' },
   { id: 3, name: 'Get Product Templates', refId: 'getProductTemplates' },
   { id: 4, name: 'Update Product Template', refId: 'updateProductTemplate' },
   { id: 5, name: 'Delete Product Template', refId: 'deleteProductTemplate' },
];

const productVariantRequests: selectDataType[] = [
   { id: 1, name: 'Create Product Variant', refId: 'createProductVariant', isActive: true },
   { id: 2, name: 'Get Product Variant', refId: 'getProductVariant' },
   { id: 3, name: 'Get Product Variants', refId: 'getProductVariants' },
   { id: 4, name: 'Update Product Variant', refId: 'updateProductVariant' },
   { id: 5, name: 'Delete Product Variant', refId: 'deleteProductVariant' },
];

const orderRequests: selectDataType[] = [
   { id: 1, name: 'Create Order', refId: 'createOrder', isActive: true },
   { id: 2, name: 'Get Order', refId: 'getOrder' },
   { id: 3, name: 'Get Orders', refId: 'getOrders' },
   { id: 4, name: 'Update Order', refId: 'updateOrder' },
   { id: 5, name: 'Delete Order', refId: 'deleteOrder' },
   { id: 6, name: 'Confirm Order', refId: 'confirmOrder' },
   { id: 7, name: 'Cancel Order', refId: 'cancelOrder' },
   { id: 8, name: 'Mark Invoice Paid', refId: 'markOrderPaid' },
   // { id: 9, name: 'Create Return', refId: 'createReturn' },
   // { id: 10, name: 'Create Refund', refId: 'createRefund' },
];

const customerRequests: selectDataType[] = [
   { id: 1, name: 'Create Customer', refId: 'createCustomer', isActive: true },
   { id: 2, name: 'Get Customer', refId: 'getCustomer' },
   { id: 3, name: 'Get Customers', refId: 'getCustomers' },
   { id: 4, name: 'Update Customer', refId: 'updateCustomer' },
   { id: 5, name: 'Delete Customer', refId: 'deleteCustomer' },
];

const inventoryRequests: selectDataType[] = [
   { id: 1, name: 'Get Quantity', refId: 'getInventoryQuantity', isActive: true },
   { id: 2, name: 'Get Quantities', refId: 'getInventoryQuantities' },
   { id: 3, name: 'Set Quantity', refId: 'setInventoryQuantity' },
];

const functionDocs: Record<string, string> = {
   createProductTemplate: 'Payload JSON object for product.template.\nExample: { "name": "Sample Product", "list_price": 99, "sale_ok": true }',
   getProductTemplate: 'Requires Template ID. Optional Query: { "fields": ["id", "name", "list_price"] }',
   getProductTemplates: 'Optional Query: { "limit": 50, "domain": [["sale_ok", "=", true]] }',
   updateProductTemplate: 'Requires Template ID and payload JSON object.',
   deleteProductTemplate: 'Requires Template ID.',
   createProductVariant: 'Payload JSON object for product.product. Include product_tmpl_id when creating variants directly.',
   getProductVariant: 'Requires Variant ID. Optional Query can include fields.',
   getProductVariants: 'Optional Query: { "limit": 50, "domain": [["active", "=", true]] }',
   updateProductVariant: 'Requires Variant ID and payload JSON object.',
   deleteProductVariant: 'Requires Variant ID.',
   createCustomer: 'Payload JSON object for res.partner.\nExample: { "name": "Alice", "email": "alice@example.com", "customer_rank": 1 }',
   getCustomer: 'Requires Customer ID.',
   getCustomers: 'Optional Query: { "limit": 50, "domain": [["customer_rank", ">", 0]] }',
   updateCustomer: 'Requires Customer ID and payload JSON object.',
   deleteCustomer: 'Requires Customer ID.',
   createOrder: 'Payload JSON object for sale.order.\nExample: { "partner_id": 301, "order_line": [[0, 0, { "product_id": 201, "product_uom_qty": 2, "price_unit": 99 }]] }\nRequired: partner_id and at least one order_line entry.',
   getOrder: 'Requires Order ID. Optional Query can include fields.',
   getOrders: 'Optional Query: { "limit": 50, "domain": [["state", "=", "sale"]] }',
   updateOrder: 'Requires Order ID and payload JSON object.',
   deleteOrder: 'Requires Order ID.',
   confirmOrder: 'Requires Order ID. Calls sale.order action_confirm.',
   cancelOrder: 'Requires Order ID. Calls sale.order action_cancel.',
   markOrderPaid: 'Requires Invoice ID.\nUse this on a posted customer invoice.\nOptional Payload Example: { "amount": 99, "payment_date": "2026-06-15", "journal_id": 12, "payment_method_line_id": 3, "communication": "Workflow payment" }\nResponse returns payment registration details, or an `alreadyPaid` flag if the invoice is already settled.',
   createReturn: 'Requires Picking ID and payload JSON object.\nThe picking must be completed (`state = done`).\nPayload Example: { "product_return_moves": [] }\nUse the outbound delivery picking for the order you want to return.',
   createRefund: 'Requires Invoice ID and payload JSON object.\nUse this on a posted customer invoice.\nPayload Example: { "reason": "Customer return", "date": "2026-06-15", "journal_id": 12 }\nDo not send order creation fields like `partner_id` or `order_line` here.\nResponse returns the created reversal / credit note details.',
   getInventoryQuantity: 'Requires Product ID. Location ID is optional.',
   getInventoryQuantities: 'Optional Query: { "limit": 50, "domain": [["location_id", "=", 8]] }',
   setInventoryQuantity: 'Requires Product ID, Location ID, and Quantity. Quantity is absolute, not a delta.',
};

export function Odoo({ id, data, setChildMinimize, setChildSettings }: OdooProps) {
   const [resources, setResources] = React.useState<selectDataType[]>(resourceOptionsBase);
   const [requests, setRequests] = React.useState<selectDataType[]>(leadRequests);
   const { setNodes } = useReactFlow();
   const conn = data.value?.connection;
   const getOdooCredentials = () => conn ? {
      url: conn.url,
      db: conn.db,
      username: conn.username,
      password: conn.password || conn.apiKey,
      apiKey: conn.apiKey
   } : null;
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const reqStatus = React.useRef({ columns: false, leads: false, notes: false, contacts: false });

   // Get requests based on current resource
   const getRequestsForResource = (resource: string) => {
      switch (resource) {
         case 'note': return noteRequests;
         case 'contact': return contactRequests;
         case 'productTemplate': return productTemplateRequests;
         case 'productVariant': return productVariantRequests;
         case 'order': return orderRequests;
         case 'customer': return customerRequests;
         case 'inventory': return inventoryRequests;
         default: return leadRequests;
      }
   };

   const getResourceFromRequest = (request?: string): OdooResource => {
      if (!request) return 'lead';
      const allResources: OdooResource[] = ['lead', 'note', 'contact', 'productTemplate', 'productVariant', 'order', 'customer', 'inventory'];
      return allResources.find(resource => getRequestsForResource(resource).some(item => item.refId === request)) || 'lead';
   };

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // Initialize resource and request
   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, { resource: 'lead', request: 'fetch_attachments' });
      } else {
         const currentResource = data.value.resource || getResourceFromRequest(data.value.request);
         setResources(resources => resources.map(r => ({ ...r, isActive: r.refId === currentResource })));
         const reqs = getRequestsForResource(currentResource);
         setRequests(reqs.map(r => ({ ...r, isActive: r.refId === data.value.request })));
         if (!data.value.resource && data.value.request) {
            updateNodeValue(id, setNodes, { resource: currentResource });
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // Fetch columns on mount if connection exists but columns are empty
   React.useEffect(() => {
      if (conn && !data.value?.columns?.length && data.value?.resource === 'lead') {
         const fetchColumns = async () => {
            try {
               const response = await axios.post(`${webURL}/crm/odoo/pipeline/columns`, getOdooCredentials());
               const columns = response.data;
               reqStatus.current.columns = true;
               updateNodeValue(id, setNodes, {
                  columns: columns.map((item: any, index: number) => ({
                     id: item.id, name: item.name, isActive: index === 0
                  }))
               });
            } catch (err: any) {
               console.error('Failed to fetch Odoo columns:', err.response?.data || err.message || err);
            }
         };
         fetchColumns();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [conn]);

   // Fetch leads when columns change (for Lead and Note resource)
   React.useEffect(() => {
      if (reqStatus.current.columns && data.value?.columns?.length && (data.value?.resource === 'lead' || data.value?.resource === 'note')) {
         const fetchData = async () => {
            reqStatus.current.columns = false;
            const columnId = data.value.columns.find((item: any) => item.isActive)?.id;
            if (!columnId) return;
            const leads = (await axios.post(`${webURL}/crm/odoo/leads/${columnId}`, getOdooCredentials())).data;
            updateNodeValue(id, setNodes, { leads: leads.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
         }
         fetchData();
      } // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.columns]);

   // Handle connection authentication
   const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, { connection: connectionData });
         const creds = {
            url: connectionData.url,
            db: connectionData.db,
            username: connectionData.username,
            password: connectionData.password || connectionData.apiKey,
            apiKey: connectionData.apiKey
         };
         try {
            // Fetch columns for Lead resource
            const response = await axios.post(`${webURL}/crm/odoo/pipeline/columns`, creds);
            const columns = response.data;
            reqStatus.current.columns = true;
            updateNodeValue(id, setNodes, { columns: columns.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
         } catch (err: any) {
            console.error('Failed to fetch Odoo columns:', err.response?.data || err.message || err);
         }
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { connection: null, columns: [], leads: [], notes: [], contacts: [] });
      }
   }

   // Handle input changes
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string, isInfo?: boolean) => {
      if (keyRef) {
         if (isInfo) setNodes(nodes => nodes.map(node => {
            if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, info: { ...node.data.value.info, [keyRef]: e.target.value } } } }
            else return node;
         }));
         else updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
      }
   }

   // Handle resource selection
   const handleSelectResource = async (selectId: number) => {
      const refId = resources.find(r => r.id === selectId)?.refId || 'lead';
      setResources(resources => resources.map(r => ({ ...r, isActive: r.id === selectId })));

      // Update requests based on selected resource
      const newRequests = getRequestsForResource(refId);
      setRequests(newRequests.map((r, i) => ({ ...r, isActive: i === 0 })));

      // Reset data when resource changes
      updateNodeValue(id, setNodes, {
         resource: refId,
         request: newRequests[0].refId,
         notes: [],
         contacts: []
      });

      // Fetch notes or contacts if needed
      if (refId === 'note' && conn) {
         try {
            const notes = (await axios.post(`${webURL}/crm/odoo/notes`, getOdooCredentials())).data;
            updateNodeValue(id, setNodes, { notes: notes.map((item: any, index: number) => ({ id: item.id, name: item.name || `Note ${item.id}`, isActive: index === 0 })) });
         } catch (err) {
            console.error('Failed to fetch notes:', err);
         }
      }
      // Fetch contacts if needed
      if (refId === 'contact' && conn) {
         try {
            // Use webURL to call Node.js backend directly
            const contacts = (await axios.post(`${webURL}/crm/odoo/contacts`, getOdooCredentials())).data;
            updateNodeValue(id, setNodes, { contacts: contacts.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
         } catch (err) {
            console.error('Failed to fetch contacts:', err);
         }
      }
      // Fetch columns and leads if needed
      if (refId === 'lead' && conn) {
         try {
            const response = await axios.post(`${webURL}/crm/odoo/pipeline/columns`, getOdooCredentials());
            const columns = response.data;
            updateNodeValue(id, setNodes, { columns: columns.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
            const firstColumnId = columns[0]?.id;
            if (firstColumnId) {
               const leads = (await axios.post(`${webURL}/crm/odoo/leads/${firstColumnId}`, getOdooCredentials())).data;
               updateNodeValue(id, setNodes, { leads: leads.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
            }
         } catch (err) {
            console.error('Failed to fetch columns/leads:', err);
         }
      }
   };

   // Helper function to refresh contacts list
   const refreshContacts = async () => {
      if (!conn) return;
      try {
         const contacts = (await axios.post(`${webURL}/crm/odoo/contacts`, getOdooCredentials())).data;
         updateNodeValue(id, setNodes, { contacts: contacts.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
      } catch (err) {
         console.error('Failed to refresh contacts:', err);
      }
   };

   // Helper function to refresh pipeline columns
   const refreshColumns = async () => {
      if (!conn) return;
      try {
         const response = await axios.post(`${webURL}/crm/odoo/pipeline/columns`, getOdooCredentials());
         const columns = response.data;
         updateNodeValue(id, setNodes, { columns: columns.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
      } catch (err) {
         console.error('Failed to refresh columns:', err);
      }
   };

   // Helper function to refresh leads for the active column
   const refreshLeads = async () => {
      if (!conn) return;
      try {
         const columnId = data.value?.columns?.find((item: any) => item.isActive)?.id;
         if (!columnId) return;
         const leads = (await axios.post(`${webURL}/crm/odoo/leads/${columnId}`, getOdooCredentials())).data;
         updateNodeValue(id, setNodes, { leads: leads.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
      } catch (err) {
         console.error('Failed to refresh leads:', err);
      }
   };

   // Handle request and other selections
   const handleSelectRequests = async (action: 'request' | 'columns' | 'leads' | 'notes' | 'contacts', selectId: number) => {
      if (action === 'request') {
         const refId = requests.find(obj => obj.id === selectId)?.refId;
         setRequests((requests) => requests.map((request) => {
            if (request.id === selectId) return { ...request, isActive: true };
            else return { ...request, isActive: false };
         }));
         setNodes(nodes => nodes.map(node => {
            if (node.id === id) {
               return { ...node, data: { ...node.data, value: { ...node.data.value, request: refId } } }
            } else return node;
         }));

         // Auto-refresh contacts when switching to update/delete contact requests
         if (['update_contact', 'delete_contact', 'get_contact'].includes(refId || '')) {
            await refreshContacts();
         }
         // Auto-refresh leads when switching to lead detail/update/delete requests
         if (['lead_details', 'update_lead', 'delete_lead'].includes(refId || '')) {
            await refreshLeads();
         }
      } else if (action === 'columns' || action === 'leads' || action === 'notes' || action === 'contacts') {
         const arr = data.value[action]?.map((item: any) => ({ ...item, isActive: item.id === selectId })) || [];
         if (action === 'columns') reqStatus.current.columns = true;
         updateNodeValue(id, setNodes, { [action]: arr });

         // Fetch notes when a lead is selected in Note resource mode
         if (action === 'leads' && data.value?.resource === 'note' && conn) {
            try {
               // Use webURL to call Node.js backend directly (not through Python API proxy)
               const notes = (await axios.post(`${webURL}/crm/odoo/notes/${selectId}`, getOdooCredentials())).data;
               // Strip HTML tags from body for cleaner display
               const stripHtml = (html: string) => html?.replace(/<[^>]*>/g, '') || '';
               updateNodeValue(id, setNodes, { notes: notes.map((item: any, index: number) => ({ id: item.id, name: stripHtml(item.body)?.substring(0, 30) || `Note ${item.id}`, isActive: index === 0 })) });
            } catch (err) {
               console.error('Failed to fetch notes:', err);
               updateNodeValue(id, setNodes, { notes: [] });
            }
         }
      }
   }

   // Handle button actions
   const handleButtons = async (type: 'attachments' | 'download', value?: any) => {
      try {
         if (type === 'attachments') {
            updateNodeValue(id, setNodes, { isLoading: true });
            const columnId = data.value.columns.find((item: any) => item.isActive).id;
            const response = (await axios.post(`${webURL}/crm/odoo/attachments/${columnId}`, getOdooCredentials())).data;
            updateNodeValue(id, setNodes, {
               isLoading: false, attachments: response.attachments.map((file: any) => ({ id: file.id, name: file.name, type: file.mimetype }))
            });
         } else if (type === 'download') {
            const filter = data.value.attachments.filter((file: any) => file.isActive && !file.url).map((file: any) => file.id);
            if (filter.length) {
               updateNodeValue(id, setNodes, { isLoading: true });
               const files: { id: number, url: string }[] = [];
               for (const fileId of filter) {
                  const result = (await axios.post(`${webURL}/crm/odoo/attachments/${fileId}/download`, getOdooCredentials())).data;
                  files.push({ id: fileId, url: result.url });
               }
               const reMap = data.value.attachments.map((file: any) => ({ ...file, url: file.url || files.find((f) => f.id === file.id)?.url || '' }))
               updateNodeValue(id, setNodes, { isLoading: false, attachments: reMap });
               files.forEach(file => window.open(file.url, '_blank'));
            } else {
               const activeFiles = data.value.attachments.filter((file: any) => file.isActive && file.url);
               activeFiles.forEach((file: any) => window.open(file.url, '_blank'));
            }
         }
      } catch {
         updateNodeValue(id, setNodes, { isLoading: false });
      }
   }

   // Computed values
   const currentResource = data.value?.resource || 'lead';
   const currentRequest = data.value?.request || '';
   const attachmentsLengthActive = currentRequest === 'fetch_attachments' ? data.value?.attachments?.filter((item: any) => item.isActive)?.length : 0;
   const isWarning = !data.value?.connection;

   // Check if we need to show specific selectors
   const showColumnId = currentResource === 'lead' || currentResource === 'note';
   const showLeadId = (currentResource === 'lead' && ['update_lead', 'lead_details', 'delete_lead'].includes(currentRequest)) ||
      (currentResource === 'note' && ['create_note', 'update_note', 'delete_note', 'get_note'].includes(currentRequest));
   const showNoteId = currentResource === 'note' && ['get_note', 'update_note', 'delete_note'].includes(currentRequest);
   const showContactId = currentResource === 'contact' && ['get_contact', 'update_contact', 'delete_contact'].includes(currentRequest);

   // Check if we need to show form fields
   const showLeadFields = currentResource === 'lead' && ['new_lead', 'update_lead'].includes(currentRequest);
   const showNoteFields = currentResource === 'note' && ['create_note', 'update_note'].includes(currentRequest);
   const showContactFields = currentResource === 'contact' && ['create_contact', 'update_contact'].includes(currentRequest);

   const showTemplateId = ['getProductTemplate', 'updateProductTemplate', 'deleteProductTemplate'].includes(currentRequest);
   const showVariantId = ['getProductVariant', 'updateProductVariant', 'deleteProductVariant'].includes(currentRequest);
   const showCustomerId = currentResource === 'customer' && ['getCustomer', 'updateCustomer', 'deleteCustomer'].includes(currentRequest);
   const showOrderId = ['getOrder', 'updateOrder', 'deleteOrder', 'confirmOrder', 'cancelOrder'].includes(currentRequest);
   const showInvoiceId = ['markOrderPaid', 'createRefund'].includes(currentRequest);
   const showPickingId = currentRequest === 'createReturn';
   const showProductId = ['getInventoryQuantity', 'setInventoryQuantity'].includes(currentRequest);
   const showLocationId = ['getInventoryQuantity', 'setInventoryQuantity'].includes(currentRequest);
   const showQuantity = currentRequest === 'setInventoryQuantity';
   const showPayload = [
      'createProductTemplate', 'updateProductTemplate',
      'createProductVariant', 'updateProductVariant',
      'createCustomer', 'updateCustomer',
      'createOrder', 'updateOrder',
      'markOrderPaid', 'createReturn', 'createRefund'
   ].includes(currentRequest);
   const showQuery = [
      'getProductTemplate', 'getProductTemplates',
      'getProductVariant', 'getProductVariants',
      'getCustomer', 'getCustomers',
      'getOrder', 'getOrders',
      'getInventoryQuantity', 'getInventoryQuantities'
   ].includes(currentRequest);

   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ width: '420px' }}
         ><Styled>
               <AuthenticateSelect type="odoo" defaultId={data.value?.connection?.id} handleAuthenticate={handleAuthenticate} background={data.background} />

               {/* Action buttons for fetch_attachments */}
               <div className="action">
                  {data.value?.connection && currentRequest === 'fetch_attachments' ? <>
                     <button className="btn1 withBorder" onClick={_ => handleButtons('attachments')} disabled={data.value?.isLoading}>Fetch</button>
                     {attachmentsLengthActive ? <button className="btn1 withBorder" onClick={_ => handleButtons('download')} disabled={data.value?.isLoading}>Download</button> : null}
                  </> : null}
               </div>

               {/* Resource Select */}
               <Select title="Resource" data={resources} handleSelectItem={(id) => handleSelectResource(id)} isSmall={true} />

               {/* Request Select */}
               <Select title="Request" data={requests} handleSelectItem={(id) => handleSelectRequests('request', id)} isSmall={true} />

               {/* Column Id (Lead only) */}
               {showColumnId && <Select title="Column Id" data={data.value?.columns} handleSelectItem={(id) => handleSelectRequests('columns', id)} isSmall={true} />}

               {/* Lead Id */}
               {showLeadId && <Select title="Lead Id" data={data.value?.leads} handleSelectItem={(id) => handleSelectRequests('leads', id)} isSmall={true} />}

               {/* Note Id */}
               {showNoteId && <Select title="Note Id" data={data.value?.notes} handleSelectItem={(id) => handleSelectRequests('notes', id)} isSmall={true} />}

               {/* Contact Id */}
               {showContactId && <Select title="Contact Id" data={data.value?.contacts} handleSelectItem={(id) => handleSelectRequests('contacts', id)} isSmall={true} />}

               {/* Lead form fields */}
               {showLeadFields && <div className="scroll nowheel">
                  <TextInput keyRef="name" title="Name" placeholder="lead_name" value={data.value?.info?.name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="expected_revenue" title="Revenue" placeholder="expected_revenue" value={data.value?.info?.expected_revenue} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="probability" title="Probability" placeholder="probability" value={data.value?.info?.probability} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="customer" title="Customer" placeholder="customer" value={data.value?.info?.customer} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="email" title="Email" placeholder="email" value={data.value?.info?.email} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="phone" title="Phone" placeholder="phone" value={data.value?.info?.phone} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="salesperson" title="Salesperson" placeholder="salesperson" value={data.value?.info?.salesperson} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="expected_closing" title="Closing" placeholder="expected_closing" value={data.value?.info?.expected_closing} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="company_name" title="Company" placeholder="company_name" value={data.value?.info?.company_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="address" title="Address" placeholder="address" value={data.value?.info?.address} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="contact_name" title="Contact" placeholder="contact_name" value={data.value?.info?.contact_name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="job_position" title="Job" placeholder="job_position" value={data.value?.info?.job_position} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="website" title="Website" placeholder="website" value={data.value?.info?.website} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="campaign" title="Campaign" placeholder="campaign" value={data.value?.info?.campaign} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="medium" title="Medium" placeholder="medium" value={data.value?.info?.medium} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="sales_team" title="Sales" placeholder="sales_team" value={data.value?.info?.sales_team} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
               </div>}

               {/* Note form fields */}
               {showNoteFields && <div style={{ marginTop: '15px', paddingTop: '15px', overflow: 'visible' }}>
                  <Textarea keyRef="body" title="Body" placeholder="Note content..." value={data.value?.info?.body} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
               </div>}

               {/* Contact form fields */}
               {showContactFields && <div className="scroll nowheel">
                  <TextInput keyRef="name" title="Name" placeholder="Contact name" value={data.value?.info?.name} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="email" title="Email" placeholder="email@example.com" value={data.value?.info?.email} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="phone" title="Phone" placeholder="+1234567890" value={data.value?.info?.phone} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="company" title="Company" placeholder="Company name" value={data.value?.info?.company} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
                  <TextInput keyRef="address" title="Address" placeholder="Full address" value={data.value?.info?.address} handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)} />
               </div>}

               {showTemplateId && <TextInput keyRef="templateId" title="Template ID" placeholder="product.template id" value={data.value?.templateId} handleChange={handleChangeInput} />}
               {showVariantId && <TextInput keyRef="variantId" title="Variant ID" placeholder="product.product id" value={data.value?.variantId} handleChange={handleChangeInput} />}
               {showCustomerId && <TextInput keyRef="customerId" title="Customer ID" placeholder="res.partner id" value={data.value?.customerId} handleChange={handleChangeInput} />}
               {showOrderId && <TextInput keyRef="orderId" title="Order ID" placeholder="sale.order id" value={data.value?.orderId} handleChange={handleChangeInput} />}
               {showInvoiceId && <TextInput keyRef="invoiceId" title="Invoice ID" placeholder="account.move id" value={data.value?.invoiceId} handleChange={handleChangeInput} />}
               {showPickingId && <TextInput keyRef="pickingId" title="Picking ID" placeholder="stock.picking id" value={data.value?.pickingId} handleChange={handleChangeInput} />}
               {showProductId && <TextInput keyRef="productId" title="Product ID" placeholder="product.product id" value={data.value?.productId} handleChange={handleChangeInput} />}
               {showLocationId && <TextInput keyRef="locationId" title="Location ID" placeholder="stock.location id" value={data.value?.locationId} handleChange={handleChangeInput} />}
               {showQuantity && <TextInput keyRef="quantity" title="Quantity" placeholder="10" value={data.value?.quantity} handleChange={handleChangeInput} />}

               {showPayload && <Textarea
                  keyRef="payload"
                  title="Payload"
                  placeholder={'{\n  "name": "Sample"\n}'}
                  value={data.value?.payload}
                  handleChange={(e) => updateNodeValue(id, setNodes, { payload: e.target.value })}
               />}

               {showQuery && <Textarea
                  keyRef="query"
                  title="Query"
                  placeholder={'{\n  "limit": 50,\n  "domain": []\n}'}
                  value={data.value?.query}
                  handleChange={(e) => updateNodeValue(id, setNodes, { query: e.target.value })}
               />}

               {functionDocs[currentRequest] ? <div className="previewSection">
                  <h5>Function Block Preview</h5>
                  <pre>{functionDocs[currentRequest]}</pre>
               </div> : null}

               {/* Attachments section */}
               {currentRequest === 'fetch_attachments' ? <>
                  {data.value?.attachments?.length ? <div className="filesEdit">
                     <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Attachments ({attachmentsLengthActive}/{data.value.attachments.length})</h3>
                     <div className="scroll nowheel">
                        <Checkboxs
                           data={data.value.attachments.map((file: any) => ({ ...file, isNotActive: !file.url }))}
                           handelCheckbox={(fileId: any, value?: boolean) => {
                              updateNodeValue(id, setNodes, {
                                 attachments: data.value.attachments.map((item: any) => {
                                    if (item.id === fileId) return ({ ...item, isActive: !value })
                                    else return item;
                                 })
                              });
                           }}
                        />
                     </div>
                  </div> : null}
                  <Switch title="Start autopilot (with loop)" className="autopilot" value={!!data.value?.isAutopilot} setValue={() => updateNodeValue(id, setNodes, { isAutopilot: !data.value?.isAutopilot })} />
                  <p>Please select your keywords carefully to avoid downloading incorrect or unintended files!</p>
               </> : null}
            </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}
